import * as Portal from "@radix-ui/react-portal";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  HelpCircle,
  Loader,
  PauseCircle,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { BackgroundJob } from "@/apis/gql/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type { TranslationKey } from "@/localisation/types";
import { useBackgroundJobs } from "@/providers/background-jobs";
import { isEqual } from "lodash";
import { TypographySmall } from "../../ui/typography";

const _isEqual = (a: string[], b: string[]) => {
  const _result = isEqual(a.sort(), b.sort());
  return _result;
};

const getJobStatus = (
  job: BackgroundJob<{ entityIds: string[] }, string[]>,
) => {
  if (
    job.status === "failed" ||
    (job.returnvalue?.value
      ? !_isEqual(job.returnvalue.value, job.data.entityIds)
      : false)
  ) {
    return "failed";
  }

  return job.status;
};

const getStatusIcon = (status: BackgroundJob["status"]) => {
  switch (status) {
    case "completed":
      return <CheckCircle className="text-green-500" size={16} />;
    case "failed":
      return <XCircle className="text-red-500" size={16} />;
    case "active":
    case "waiting-children":
      return <Loader className="text-gray-500 animate-spin" size={16} />;
    case "waiting":
    case "delayed":
      return <Clock className="text-yellow-500" size={16} />;
    case "paused":
      return <PauseCircle className="text-orange-500" size={16} />;
    case "stuck":
      return <AlertCircle className="text-red-500" size={16} />;
    default:
      return <HelpCircle className="text-gray-500" size={16} />;
  }
};

export function ExportBackgroundJobStatusTracker() {
  const { backgroundJobs: jobs, clearBackgroundJobs } = useBackgroundJobs<
    { entityIds: string[] },
    string[]
  >({
    pick: ["buchungsdatenservice-export"],
  });

  const { t } = useTranslation();

  const [value, setValue] = useState<string>("");

  useEffect(() => {
    if (jobs.length > 0) {
      setValue("jobs");
    }
  }, [jobs]);

  const handleOnValueChange = (value: string) => {
    setValue(value);
  };

  const handleClose = () => {
    clearBackgroundJobs();
    setValue("");
  };

  const styles = useMemo(() => {
    if (
      jobs.some((job) => {
        return (
          job.status === "failed" ||
          (job.returnvalue?.value
            ? !_isEqual(job.returnvalue.value, job.data.entityIds)
            : false)
        );
      })
    ) {
      return {
        backgroundColor: "bg-red-500",
        textColor: "text-red-700",
        label: t(
          "integrations.datev.background_job_titles.failed.title",
        ) as string,
        description: t(
          "integrations.datev.background_job_titles.failed.description",
        ) as string,
      };
    }

    if (jobs.some((job) => job.status === "active")) {
      return {
        backgroundColor: "bg-blue-500",
        label: t("integrations.datev.background_job_titles.active.title"),
      };
    }

    if (jobs.every((job) => job.status === "completed")) {
      return {
        backgroundColor: "bg-green-500",
        label: t("integrations.datev.background_job_titles.completed.title"),
      };
    }

    return {
      backgroundColor: "bg-blue-500",
      label: t("integrations.datev.background_job_titles.active.title"),
    };
  }, [jobs, t]);

  if (jobs.length === 0) {
    return null;
  }

  return (
    <Portal.Root container={document.getElementById("status-cards")}>
      <Accordion
        type="single"
        collapsible
        value={value}
        onValueChange={handleOnValueChange}
        disabled={jobs.length === 0}
      >
        <AccordionItem value="jobs" className="border-none">
          <Card
            className={cn("shadow-lg overflow-hidden", styles.backgroundColor)}
            style={{
              width: "450px",
            }}
          >
            <div className="flex flex-1 items-center justify-between py-3 px-2">
              <div className="flex-1">
                <AccordionTrigger className="p-0 w-full [&[data-state=open]]:no-underline hover:no-underline [&>svg]:text-primary">
                  <CardHeader className={cn("p-1 w-full text-white")}>
                    <CardTitle
                      className={cn(
                        "w-full text-start",
                        styles.backgroundColor,
                      )}
                    >
                      {styles.label}
                    </CardTitle>
                  </CardHeader>
                </AccordionTrigger>
              </div>
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: no comments */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="hover:bg-gray-100 p-1 rounded-full ml-2"
              >
                <X size={16} />
              </div>
            </div>
            <AccordionContent className="pb-0">
              <CardContent className="px-4 py-4 space-y-4 bg-background">
                {styles.description && (
                  <TypographySmall
                    className={cn(
                      "text-start font-normal opacity-70",
                      styles.textColor,
                    )}
                  >
                    {styles.description}
                  </TypographySmall>
                )}
                {jobs.map((job) => {
                  const status = getJobStatus(job);

                  return (
                    <div key={job.jobId} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center justify-center space-x-2">
                          {getStatusIcon(status)}
                          <span className="font-medium">
                            {
                              t(
                                `background_job_name.${job.jobName}` as TranslationKey,
                              ) as string
                            }
                          </span>
                          {job.meta?.label && (
                            <span className="text-xs text-gray-500">
                              {t(job.meta.label as any) as string}
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-gray-500 capitalize">
                          {
                            t(
                              `background_job_status.${status}` as TranslationKey,
                            ) as string
                          }
                        </span>
                      </div>
                      <Progress value={job.progress} className="h-2" />
                    </div>
                  );
                })}
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>
      </Accordion>
    </Portal.Root>
  );
}
